import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { AI_AGENT_UI } from './tokens';

export type AgentChatSidebarItem = {
  id: number;
  title: string;
  preview?: string;
};

type Props = {
  chats: AgentChatSidebarItem[];
  activeChatId: number | null;
  onSelect: (id: number) => void;
  onNewChat: () => void;
  onChatMenu?: (event: React.MouseEvent<HTMLElement>, chat: AgentChatSidebarItem) => void;
  openMenuChatId?: number | null;
};

/** Left chats panel — AI Agent page only. */
export function AgentChatSidebar({
  chats,
  activeChatId,
  onSelect,
  onNewChat,
  onChatMenu,
  openMenuChatId,
}: Props) {
  return (
    <Box
      sx={{
        width: { xs: '100%', md: AI_AGENT_UI.sidebarWidthMd, lg: 228, xl: AI_AGENT_UI.sidebarWidth },
        minWidth: { md: AI_AGENT_UI.sidebarWidthMd, lg: 228, xl: AI_AGENT_UI.sidebarWidth },
        maxWidth: { md: AI_AGENT_UI.sidebarWidthMd, lg: 228, xl: AI_AGENT_UI.sidebarWidth },
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: AI_AGENT_UI.surface,
        borderRight: { md: `1px solid ${AI_AGENT_UI.border}` },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1,
          pt: 1.25,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexShrink: 0,
          minHeight: 36,
          borderBottom: `1px solid ${AI_AGENT_UI.border}`,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.875rem',
            color: AI_AGENT_UI.text,
            letterSpacing: '-0.01em',
            height: 36,
            lineHeight: '36px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          AI Chats
        </Typography>
        <Button
          size="small"
          variant="contained"
          onClick={onNewChat}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            borderRadius: '8px',
            px: 1,
            bgcolor: AI_AGENT_UI.primary,
            boxShadow: 'none',
            height: 32,
            minHeight: 32,
            flexShrink: 0,
            '&:hover': {
              bgcolor: AI_AGENT_UI.primaryDark,
              boxShadow: 'none',
            },
          }}
        >
          New Chat
        </Button>
      </Box>

      <Typography
        sx={{
          px: 1,
          pb: 0.75,
          fontSize: AI_AGENT_UI.fontSm,
          fontWeight: 500,
          color: AI_AGENT_UI.textSecondary,
          flexShrink: 0,
        }}
      >
        Your chats
      </Typography>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 0.75, pb: 1.5 }}>
        {chats.length === 0 ? (
          <Typography
            sx={{
              px: 1,
              py: 2,
              fontSize: AI_AGENT_UI.fontSm,
              color: AI_AGENT_UI.textSecondary,
              textAlign: 'center',
            }}
          >
            No chats yet. Start a conversation.
          </Typography>
        ) : (
          chats.map((chat) => {
            const selected = chat.id === activeChatId;
            return (
              <Box
                key={chat.id}
                onClick={() => onSelect(chat.id)}
                sx={{
                  mb: 0.35,
                  px: 0.75,
                  py: 0.65,
                  borderRadius: `${AI_AGENT_UI.radius.sm}px`,
                  bgcolor: selected ? AI_AGENT_UI.selected : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'background-color 0.12s ease',
                  '&:hover': {
                    bgcolor: selected ? AI_AGENT_UI.selected : AI_AGENT_UI.surfaceMuted,
                  },
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: AI_AGENT_UI.font,
                    fontWeight: selected ? 600 : 500,
                    color: AI_AGENT_UI.text,
                    lineHeight: 1.35,
                  }}
                >
                  {chat.title || 'New chat'}
                </Typography>
                {onChatMenu && (
                  <IconButton
                    size="small"
                    aria-label="Chat actions"
                    aria-expanded={openMenuChatId === chat.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChatMenu(e, chat);
                    }}
                    sx={{
                      color: AI_AGENT_UI.textSecondary,
                      width: 28,
                      height: 28,
                      flexShrink: 0,
                      opacity: selected || openMenuChatId === chat.id ? 1 : 0.7,
                      '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.06)' },
                    }}
                  >
                    <MoreHorizIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
